/**
 * Mentions in the transcript — the old web's `MentionRenderer.tsx`, the
 * read-only twin of the editor's chip. A user message is split at its markers
 * into text and `<MentionChip>`s (no markdown: the user's own words are shown
 * as typed); an assistant message is markdown with `rehypeMentions` doing the
 * same inside the hast tree.
 *
 * The chip's colour asks the document context whether the item has
 * parameters. It never *fetches* to find out: old messages may name elements
 * that no longer exist, and warming the cache from history would be a burst
 * of 404s for nothing.
 */
import { useDocumentContextCtx } from "../../contexts/DocumentContextProvider";
import {
  MENTION_WRAPPER_CLASS,
  MINOR_CHIP_CLASS,
  MINOR_CHIP_OVERFLOW_CLASS,
  getMajorChipClass,
  getMajorChipText,
  getMajorChipTooltip,
  getMinorChipTooltip,
  getOverflowTooltip,
  splitParamsForDisplay,
} from "../../lib/mentionChipPresentation";
import {
  MENTION_PATTERN_SOURCE,
  decodeLabel,
  isSkillMentionType,
  parseFamilyType,
  parseParams,
  type MentionParameter,
} from "../../lib/mentionUtils";
import { Markdown } from "./Markdown";

export function MentionChip({
  type,
  id,
  label,
  selectedParams,
  familyName,
  typeName,
}: {
  type: string;
  id: string;
  label: string;
  selectedParams?: MentionParameter[];
  familyName?: string;
  typeName?: string;
}) {
  const { extractable } = useDocumentContextCtx();
  const params = selectedParams ?? [];
  const probe = isSkillMentionType(type) ? false : extractable(type, id);
  const { visible, overflow } = splitParamsForDisplay(params);
  return (
    <span
      className={MENTION_WRAPPER_CLASS}
      data-type={type}
      data-id={id}
      data-label={label}
      data-family-name={familyName || undefined}
      data-type-name={typeName || undefined}
      data-params={params.length > 0 ? JSON.stringify(params) : undefined}
      aria-label={`Mention: ${label}, Type: ${type}, ID: ${id}`}
    >
      <span className={getMajorChipClass(probe ?? undefined)} title={getMajorChipTooltip(type, id)}>
        <span className="revit-mention-text">{getMajorChipText(label)}</span>
      </span>
      {visible.map((param) => (
        <span key={`${param.id}-${param.name}`} className={MINOR_CHIP_CLASS} title={getMinorChipTooltip(param)} data-param-id={param.id}>
          <span className="revit-mention-text">{param.name}</span>
        </span>
      ))}
      {overflow.length > 0 ? (
        <span className={MINOR_CHIP_OVERFLOW_CLASS} title={getOverflowTooltip(overflow)}>
          +{overflow.length}
        </span>
      ) : null}
    </span>
  );
}

/** Does the text carry any marker at all — the cheap check before splitting. */
export function hasMentionMarkers(content: string): boolean {
  return new RegExp(MENTION_PATTERN_SOURCE).test(content);
}

export function MentionRenderer({ content, renderRestAsMarkdown = false }: { content: string; renderRestAsMarkdown?: boolean }) {
  const { extractable } = useDocumentContextCtx();
  if (renderRestAsMarkdown) return <Markdown text={content} mentions={extractable} />;

  const pattern = new RegExp(MENTION_PATTERN_SOURCE, "g");
  const parts: Array<string | JSX.Element> = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null = null;
  while ((match = pattern.exec(content)) !== null) {
    const type = match[1] ?? "";
    const rawId = match[2] ?? "";
    const id = isSkillMentionType(type) ? decodeLabel(rawId) : rawId;
    const selectedParams = parseParams(match[4]);
    const { familyName, typeName } = parseFamilyType(match[5]);
    if (match.index > lastIndex) parts.push(content.slice(lastIndex, match.index));
    parts.push(
      <MentionChip
        key={`mention-${match.index}-${id}`}
        type={type}
        id={id}
        label={decodeLabel(match[3] ?? "")}
        {...(selectedParams.length > 0 ? { selectedParams } : {})}
        {...(familyName ? { familyName } : {})}
        {...(typeName ? { typeName } : {})}
      />,
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < content.length) parts.push(content.slice(lastIndex));
  return <>{parts}</>;
}
